import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

const CALL_LOG_PATH = '/call-log';

export function useCallDetailsRoute() {
	const history = useHistory();
	const location = useLocation();
	const callId = useRouteMatch<{ recentCallId?: string }>(`${CALL_LOG_PATH}/:recentCallId`)?.params.recentCallId;

	// Aida runs on hash history, which drops pushed `location.state`, so autoplay can't ride on the route.
	const [autoplayCallId, setAutoplayCallId] = useState<string | null>(null);
	const [lastCallId, setLastCallId] = useState<string | null>(null);
	const [playRequest, setPlayRequest] = useState(0);
	const pushedTo = useRef<string | null>(null);

	useEffect(() => {
		if (callId) {
			setLastCallId(callId);
			return;
		}
		pushedTo.current = null;
		setAutoplayCallId(null);
	}, [callId]);

	const openCall = (id: string, { autoplay = false }: { autoplay?: boolean } = {}) => {
		setAutoplayCallId(autoplay ? id : null);
		if (autoplay) {
			setPlayRequest(count => count + 1);
		}
		const target = { pathname: `${CALL_LOG_PATH}/${id}`, search: location.search };
		if (callId) {
			history.replace(target);
			if (pushedTo.current) {
				pushedTo.current = target.pathname + target.search;
			}
			return;
		}
		pushedTo.current = target.pathname + target.search;
		history.push(target);
	};

	const closeCall = ({ keepLastCall = false }: { keepLastCall?: boolean } = {}) => {
		if (!keepLastCall) {
			setLastCallId(null);
		}
		if (!callId) {
			return;
		}
		// Going back only undoes our own push; a deep link, or a filter or tab change since, would land somewhere else.
		if (pushedTo.current === location.pathname + location.search) {
			history.goBack();
		} else {
			history.replace({ pathname: CALL_LOG_PATH, search: location.search });
		}
		pushedTo.current = null;
	};

	return {
		autoplay: !!callId && autoplayCallId === callId,
		callId,
		closeCall,
		lastCallId,
		openCall,
		playRequest,
	};
}
